import Dexie, { Table } from 'dexie'
import type {
  StorageService,
  Batch,
  StorageUsage,
  CleanupResult,
  ItemMetadata,
  ValidationResult,
  BackupInfo,
} from '../contracts/storageService'
import { StorageQuotaError, StorageError } from '../contracts/storageService'

// Database schema interfaces
interface DBBatch {
  id: string
  name: string
  createdAt: Date
  updatedAt?: Date
  context?: any
  size?: number
}

interface DBBatchImage {
  id: string
  batchId: string
  file: File
  metadata?: any
  order: number
  createdAt: Date
}

interface DBMetadata {
  key: string
  value: any
  createdAt: Date
  updatedAt?: Date
}

// Dexie database class
export class ImageTaggerDB extends Dexie {
  batches!: Table<DBBatch>
  images!: Table<DBBatchImage>
  metadata!: Table<DBMetadata>

  constructor() {
    super('ImageTaggerDB')
    
    this.version(1).stores({
      batches: 'id, name, createdAt, updatedAt, size',
      images: 'id, batchId, createdAt',
      metadata: 'key, createdAt, updatedAt'
    })
  }
}

export class IndexedDBStorageService implements StorageService {
  private db: ImageTaggerDB
  private changeListeners: Array<() => void> = []

  constructor() {
    this.db = new ImageTaggerDB()
  }

  async saveBatch(batch: Batch): Promise<void> {
    try {
      // Check storage capacity before saving
      const usage = await this.getStorageUsage()
      const estimatedBatchSize = this.calculateBatchSize(batch)
      
      if (usage.used + estimatedBatchSize > usage.total * 0.9) { // 90% threshold
        throw new StorageQuotaError(
          `Not enough storage space. Required: ${Math.round(estimatedBatchSize / 1024)}KB, Available: ${Math.round(usage.available / 1024)}KB`,
          usage.used + estimatedBatchSize,
          usage.total
        )
      }

      await this.db.transaction('rw', this.db.batches, this.db.images, async () => {
        // Save batch metadata
        const dbBatch: DBBatch = {
          id: batch.id,
          name: batch.name,
          createdAt: batch.createdAt,
          updatedAt: batch.updatedAt || new Date(),
          context: batch.context,
          size: batch.size,
        }

        await this.db.batches.put(dbBatch)

        // Delete existing images for this batch
        await this.db.images.where('batchId').equals(batch.id).delete()

        // Save batch images with actual File objects
        if (batch.images && batch.images.length > 0) {
          const dbImages: DBBatchImage[] = batch.images.map((img, index) => ({
            id: img.id,
            batchId: batch.id,
            file: img.file,
            metadata: img.metadata,
            order: index,
            createdAt: new Date(),
          }))

          await this.db.images.bulkAdd(dbImages)
        }
      })

      this.notifyChange()
    } catch (error) {
      if (error instanceof StorageQuotaError) {
        throw error
      }
      throw new StorageError(`Failed to save batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async loadBatch(id: string): Promise<Batch | null> {
    try {
      const dbBatch = await this.db.batches.get(id)
      if (!dbBatch) return null

      const dbImages = await this.db.images
        .where('batchId')
        .equals(id)
        .orderBy('order')
        .toArray()

      return {
        id: dbBatch.id,
        name: dbBatch.name,
        createdAt: dbBatch.createdAt,
        updatedAt: dbBatch.updatedAt,
        context: dbBatch.context,
        size: dbBatch.size,
        images: dbImages.map(img => ({
          id: img.id,
          file: img.file, // Actual File object preserved!
          metadata: img.metadata,
        })),
      }
    } catch (error) {
      throw new StorageError(`Failed to load batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async listBatches(): Promise<Batch[]> {
    try {
      const dbBatches = await this.db.batches.orderBy('updatedAt').reverse().toArray()
      
      const batches: Batch[] = []
      for (const dbBatch of dbBatches) {
        const imageCount = await this.db.images.where('batchId').equals(dbBatch.id).count()
        batches.push({
          id: dbBatch.id,
          name: dbBatch.name,
          createdAt: dbBatch.createdAt,
          updatedAt: dbBatch.updatedAt,
          context: dbBatch.context,
          size: dbBatch.size,
          images: [], // Don't load images for list view (performance)
          imageCount, // Add count for display
        } as Batch & { imageCount: number })
      }
      
      return batches
    } catch (error) {
      throw new StorageError(`Failed to list batches: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async deleteBatch(id: string): Promise<void> {
    try {
      await this.db.transaction('rw', this.db.batches, this.db.images, async () => {
        await this.db.images.where('batchId').equals(id).delete()
        await this.db.batches.delete(id)
      })

      this.notifyChange()
    } catch (error) {
      throw new StorageError(`Failed to delete batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getStorageUsage(): Promise<StorageUsage> {
    try {
      // Use navigator.storage.estimate() for actual browser storage info
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate()
        const used = estimate.usage || 0
        const quota = estimate.quota || 1024 * 1024 * 1024 // 1GB fallback
        
        const batchCount = await this.db.batches.count()
        
        return {
          used,
          available: quota - used,
          total: quota,
          percentage: (used / quota) * 100,
          itemCount: batchCount,
        }
      }

      // Fallback for older browsers
      const batchCount = await this.db.batches.count()
      const imageCount = await this.db.images.count()
      
      // Estimate based on average sizes
      const estimatedUsed = batchCount * 1024 + imageCount * 1024 * 50 // Rough estimate
      const estimatedQuota = 50 * 1024 * 1024 // 50MB conservative estimate
      
      return {
        used: estimatedUsed,
        available: Math.max(0, estimatedQuota - estimatedUsed),
        total: estimatedQuota,
        percentage: (estimatedUsed / estimatedQuota) * 100,
        itemCount: batchCount,
      }
    } catch (error) {
      throw new StorageError(`Failed to get storage usage: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async hasSpace(requiredBytes: number): Promise<boolean> {
    try {
      const usage = await this.getStorageUsage()
      return usage.available >= requiredBytes
    } catch (error) {
      return false
    }
  }

  async cleanup(): Promise<CleanupResult> {
    try {
      const startTime = Date.now()
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)

      // Find old batches
      const oldBatches = await this.db.batches
        .where('createdAt')
        .below(thirtyDaysAgo)
        .toArray()

      let deletedItems = 0
      let freedBytes = 0

      // Delete old batches and their images
      for (const batch of oldBatches) {
        const batchSize = batch.size || 0
        await this.deleteBatch(batch.id)
        deletedItems++
        freedBytes += batchSize
      }

      const duration = Date.now() - startTime

      this.notifyChange()

      return {
        deletedItems,
        freedBytes,
        duration,
        errors: [],
      }
    } catch (error) {
      throw new StorageError(`Failed to cleanup storage: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async validateStorage(): Promise<ValidationResult> {
    try {
      const warnings: string[] = []
      const errors: string[] = []

      // Check database accessibility
      try {
        await this.db.batches.limit(1).toArray()
      } catch (error) {
        errors.push('Database is not accessible')
      }

      // Check storage usage
      const usage = await this.getStorageUsage()
      if (usage.percentage > 90) {
        warnings.push(`Storage usage is ${usage.percentage.toFixed(1)}% - consider cleanup`)
      }

      // Check for orphaned images
      const allImages = await this.db.images.toArray()
      const batchIds = new Set((await this.db.batches.toArray()).map(b => b.id))
      const orphanedImages = allImages.filter(img => !batchIds.has(img.batchId))
      
      if (orphanedImages.length > 0) {
        warnings.push(`Found ${orphanedImages.length} orphaned images`)
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        lastChecked: new Date(),
      }
    } catch (error) {
      return {
        isValid: false,
        errors: [`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        lastChecked: new Date(),
      }
    }
  }

  async createBackup(name?: string): Promise<string> {
    try {
      const timestamp = new Date().toISOString()
      const backupName = name || `backup-${timestamp}`

      const batches = await this.db.batches.toArray()
      const images = await this.db.images.toArray()
      const metadata = await this.db.metadata.toArray()

      const backup = {
        name: backupName,
        version: '1.0',
        createdAt: timestamp,
        batches,
        images: images.map(img => ({
          ...img,
          file: null, // Don't include File objects in backup (too large)
        })),
        metadata,
      }

      return JSON.stringify(backup, null, 2)
    } catch (error) {
      throw new StorageError(`Failed to create backup: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async restoreFromBackup(backupId: string): Promise<void> {
    // Implementation would restore from a stored backup
    throw new Error('Restore from backup not yet implemented')
  }

  async listBackups(): Promise<BackupInfo[]> {
    // Implementation would list stored backups
    return []
  }

  async exportData(): Promise<string> {
    return this.createBackup()
  }

  async importData(jsonData: string, merge = false): Promise<void> {
    try {
      const importData = JSON.parse(jsonData)

      if (!importData.batches) {
        throw new Error('Invalid import data: missing batches')
      }

      await this.db.transaction('rw', this.db.batches, this.db.images, this.db.metadata, async () => {
        if (!merge) {
          // Clear existing data if not merging
          await this.db.batches.clear()
          await this.db.images.clear()
          await this.db.metadata.clear()
        }

        // Import batches
        if (importData.batches) {
          await this.db.batches.bulkPut(importData.batches)
        }

        // Import images (without File objects)
        if (importData.images) {
          await this.db.images.bulkPut(importData.images)
        }

        // Import metadata
        if (importData.metadata) {
          await this.db.metadata.bulkPut(importData.metadata)
        }
      })

      this.notifyChange()
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new StorageError('Invalid JSON data provided for import')
      }
      throw new StorageError(`Failed to import data: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  onStorageChange(callback: () => void): () => void {
    this.changeListeners.push(callback)
    return () => {
      const index = this.changeListeners.indexOf(callback)
      if (index > -1) {
        this.changeListeners.splice(index, 1)
      }
    }
  }

  private notifyChange(): void {
    this.changeListeners.forEach(callback => {
      try {
        callback()
      } catch (error) {
        console.error('Storage change listener error:', error)
      }
    })
  }

  private calculateBatchSize(batch: Batch): number {
    let size = 0
    
    // Base batch data
    size += new Blob([JSON.stringify({
      id: batch.id,
      name: batch.name,
      createdAt: batch.createdAt,
      context: batch.context,
    })]).size

    // Images
    if (batch.images) {
      for (const image of batch.images) {
        size += image.file.size
        if (image.metadata) {
          size += new Blob([JSON.stringify(image.metadata)]).size
        }
      }
    }

    return size
  }
}